import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePendingQuiz } from "@/hooks/usePendingQuiz";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Sun, Moon, Menu, X, BookOpen, User, Shield, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import logoFull from "@/assets/logo.png";
import logoIcon from "@/assets/logo-icon.png";
interface NavbarProps {
  onSubscribeClick?: () => void;
}

const Navbar = ({ onSubscribeClick }: NavbarProps) => {
  const { user, isAdmin, signOut } = useAuth();
  const { hasPendingQuiz } = usePendingQuiz();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = location.pathname === "/" || location.pathname === "/editions";

  const handleLogout = async () => {
    await signOut();
    setMobileOpen(false);
    navigate("/");
  };

  const toggleTheme = async () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    if (user) {
      try {
        await supabase
          .from("profiles")
          .update({ theme_preference: newTheme })
          .eq("user_id", user.id);
      } catch {
        // Theme still changes locally even if DB save fails
      }
    }
  };

  const linkClass = (accent?: boolean) =>
    `text-xs tracking-wider uppercase ${accent ? "text-accent hover:text-accent/80" : "text-muted-foreground hover:text-foreground"} transition-colors`;

  const closeMobile = () => setMobileOpen(false);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (mobileOpen && user) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen, user]);

  // Close on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMobileOpen(false); };
    if (mobileOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const drawerLink = (to: string, label: string, Icon: typeof BookOpen, accent?: boolean, badge?: boolean) => {
    const active = location.pathname === to || location.pathname.startsWith(to + "/");
    return (
      <Link
        to={to}
        onClick={closeMobile}
        className={`group flex items-center justify-between border-b border-border px-5 py-4 transition-colors ${
          active ? "bg-secondary" : "hover:bg-secondary/50"
        }`}
      >
        <span className="flex items-center gap-3">
          <Icon className={`w-4 h-4 ${accent ? "text-accent" : "text-muted-foreground group-hover:text-foreground"}`} />
          <span className={`font-mono text-[11px] tracking-[0.2em] uppercase ${accent ? "text-accent" : "text-foreground"}`}>
            {label}
          </span>
          {badge && <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />}
        </span>
        <ArrowRight className={`w-3.5 h-3.5 transition-transform ${active ? "text-accent translate-x-0.5" : "text-muted-foreground/40 group-hover:translate-x-0.5"}`} />
      </Link>
    );
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-sm">
      <div className="max-w-5xl mx-auto px-4 h-14 sm:h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {!isHome && (
            <Link
              to="/"
              className="flex items-center gap-1 text-xs tracking-wider uppercase text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Home</span>
            </Link>
          )}
          <Link to="/" className="flex items-center">
            <img src={logoIcon} alt="aibly home" width={64} height={64} className="h-16 w-auto dark:invert sm:hidden" />
            <img src={logoFull} alt="aibly home" width={486} height={135} className="h-10 w-auto dark:invert hidden sm:block" />
          </Link>
        </div>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="text-muted-foreground hover:text-foreground transition-colors p-1.5"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {user ? (
            <>
              <Link to="/editions" className={linkClass()}>Editions</Link>
              <Link to="/profile" className={`${linkClass()} relative`}>
                Profile
                {hasPendingQuiz && (
                  <span className="absolute -top-1 -right-2 w-2 h-2 bg-accent rounded-full animate-pulse" />
                )}
              </Link>
              {isAdmin && (
                <Link to="/admin" className={linkClass(true)}>Admin</Link>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-xs tracking-wider uppercase text-muted-foreground px-2"
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" className={linkClass()}>Login</Link>
              {onSubscribeClick ? (
                <Button size="sm" onClick={onSubscribeClick} className="btn-shine rounded-none text-xs tracking-widest uppercase gap-1 px-4">
                  Subscribe <ArrowRight className="w-3 h-3" />
                </Button>
              ) : (
                <Link to="/signup">
                  <Button size="sm" className="btn-shine rounded-none text-xs tracking-widest uppercase gap-1 px-4">
                    Subscribe <ArrowRight className="w-3 h-3" />
                  </Button>
                </Link>
              )}
            </>
          )}
        </div>

        {/* Mobile controls */}
        <div className="flex sm:hidden items-center gap-2">
          <button
            onClick={toggleTheme}
            className="text-muted-foreground hover:text-foreground transition-colors p-1.5"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <button
              onClick={() => setMobileOpen(true)}
              className="text-muted-foreground hover:text-foreground transition-colors p-1.5 relative"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
            >
              <Menu className="w-5 h-5" />
              {hasPendingQuiz && (
                <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
              )}
            </button>
          ) : (
            <>
              <Link to="/login" className={linkClass()}>Login</Link>
              {onSubscribeClick ? (
                <Button size="sm" onClick={onSubscribeClick} className="btn-shine rounded-none text-xs tracking-widest uppercase gap-1 px-3">
                  Subscribe <ArrowRight className="w-3 h-3" />
                </Button>
              ) : (
                <Link to="/signup">
                  <Button size="sm" className="btn-shine rounded-none text-xs tracking-widest uppercase gap-1 px-3">
                    Subscribe <ArrowRight className="w-3 h-3" />
                  </Button>
                </Link>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile side drawer — authenticated only */}
      {user && (
        <>
          {/* Backdrop */}
          <div
            onClick={closeMobile}
            className={`sm:hidden fixed inset-0 z-40 bg-background/70 backdrop-blur-sm transition-opacity duration-300 ${
              mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            aria-hidden="true"
          />
          {/* Drawer panel */}
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className={`sm:hidden fixed top-0 right-0 z-50 h-[100dvh] w-[82vw] max-w-[340px] bg-background border-l border-border shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
              mobileOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 h-14 border-b border-border">
              <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-muted-foreground">Menu</span>
              <button
                onClick={closeMobile}
                className="text-muted-foreground hover:text-foreground transition-colors p-1.5 -mr-1.5"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User identity */}
            <div className="px-5 py-4 border-b border-border bg-secondary/30">
              <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-muted-foreground">Signed in as</p>
              <p className="font-display text-lg italic text-foreground truncate mt-0.5">
                {user.user_metadata?.full_name || user.email?.split("@")[0] || "Reader"}
              </p>
              <p className="font-mono text-[10px] text-muted-foreground truncate mt-0.5">{user.email}</p>
            </div>

            {/* Nav links */}
            <div className="flex-1 overflow-y-auto">
              {drawerLink("/editions", "Editions", BookOpen)}
              {drawerLink("/profile", "Profile", User, false, hasPendingQuiz)}
              {isAdmin && drawerLink("/admin", "Admin", Shield, true)}
            </div>

            {/* Footer actions */}
            <div className="border-t border-border">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-secondary/50 transition-colors"
              >
                <LogOut className="w-4 h-4 text-muted-foreground" />
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-foreground">Sign out</span>
              </button>
              <div className="px-5 py-3 border-t border-border">
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-muted-foreground/60">aibly · v1</p>
              </div>
            </div>
          </aside>
        </>
      )}
    </nav>
  );
};

export default Navbar;
